/**
 * Format a mission endpoint for display (list, details, search).
 * Supports GraphQL locationInput { city, state }, legacy "City, ST" string,
 * or flat departureCity / departureState (etc.) on the mission record.
 */
import {
  parseRouteLocationsFromDescription,
  stripRouteBlockFromDescription,
} from "./resourceRequestLocations";

export function formatMissionEndpoint(loc, flatCity, flatState) {
  if (flatCity != null && String(flatCity).trim() !== "") {
    const city = String(flatCity).trim();
    const st = flatState != null ? String(flatState).trim() : "";
    return st ? `${city}, ${st}` : city;
  }
  if (flatState != null && String(flatState).trim() !== "") {
    return String(flatState).trim();
  }
  if (loc == null) return "";
  if (typeof loc === "string") return loc.trim();
  if (typeof loc === "object" && !Array.isArray(loc)) {
    const city = loc.city != null ? String(loc.city).trim() : "";
    const state = loc.state != null ? String(loc.state).trim() : "";
    if (city && state) return `${city}, ${state}`;
    return city || state || "";
  }
  return "";
}

/** US state (or last segment) for filter dropdowns */
export function parseStateFromMissionLocation(loc, flatStateHint) {
  if (flatStateHint != null && String(flatStateHint).trim() !== "") {
    return String(flatStateHint).trim();
  }
  if (!loc) return "";
  if (typeof loc === "object" && !Array.isArray(loc) && loc.state != null) {
    return String(loc.state).trim();
  }
  if (typeof loc !== "string") return "";
  const parts = loc.split(",").map((p) => p.trim());
  return parts.length >= 2 ? parts[parts.length - 1] || "" : "";
}

function pickPassengerList(mission) {
  const raw =
    mission?.PassengerItem ??
    mission?.passengerItem ??
    mission?.passengerItems ??
    [];
  return Array.isArray(raw) ? raw : [];
}

function pickFlightRouteEndpoints(mission) {
  const route = mission?.flightRoute?.[0];
  if (!route || typeof route !== "object") {
    return {
      departureCity: "",
      departureState: "",
      arrivalCity: "",
      arrivalState: "",
    };
  }
  const dep = route.departure ?? {};
  const arr = route.arrival ?? {};
  return {
    departureCity: dep.city != null ? String(dep.city).trim() : "",
    departureState: dep.state != null ? String(dep.state).trim() : "",
    arrivalCity: arr.city != null ? String(arr.city).trim() : "",
    arrivalState: arr.state != null ? String(arr.state).trim() : "",
  };
}

export function getMissionVisibility(missionOrType) {
  const raw =
    typeof missionOrType === "string"
      ? missionOrType
      : missionOrType?.type;
  const value = String(raw || "").trim();
  if (value === "Private") return "Internal";
  if (value === "Internal" || value === "Public") return value;
  return "Public";
}

const PUBLIC_INTERNAL_MISSION_TYPES = new Set(["Public", "Internal", "Private"]);

function normalizeMissionTypeFields(mission) {
  let type = mission.type ?? "";
  let missionCategory = mission.missionCategory ?? "";
  if (!missionCategory && type && !PUBLIC_INTERNAL_MISSION_TYPES.has(type)) {
    missionCategory = type;
    type = "";
  }
  if (String(type).trim() === "Private") type = "Internal";
  return { type, missionCategory };
}

/**
 * Normalize getMissionbyId / list payloads for UI and MissionForm.
 * - passengerItem / PassengerItem → both keys for UI compatibility
 * - [Route] block in description → departure/arrival city & state
 * - flightRoute → departure/arrival when present
 * - description without embedded route block
 */
export function normalizeMissionRecord(mission) {
  if (!mission || typeof mission !== "object") return mission;

  const fromDesc = parseRouteLocationsFromDescription(mission.description);
  const fromRoute = pickFlightRouteEndpoints(mission);

  const departureCity =
    mission.departureCity ||
    fromRoute.departureCity ||
    fromDesc.locationCity ||
    "";
  const departureState =
    mission.departureState ||
    fromRoute.departureState ||
    fromDesc.locationState ||
    "";
  const arrivalCity =
    mission.arrivalCity || fromRoute.arrivalCity || fromDesc.arrivalCity || "";
  const arrivalState =
    mission.arrivalState ||
    fromRoute.arrivalState ||
    fromDesc.arrivalState ||
    "";

  const descriptionPlain = stripRouteBlockFromDescription(mission.description);
  const passengerList = pickPassengerList(mission);
  const { type, missionCategory } = normalizeMissionTypeFields(mission);
  const cargoItems = Array.isArray(mission.cargoItems)
    ? mission.cargoItems.map((item) => ({
        ...item,
        count: item.count ?? item.quantity ?? "",
        quantity: item.quantity ?? item.count ?? "",
      }))
    : [];

  return {
    ...mission,
    PassengerItem: passengerList,
    passengerItem: passengerList,
    cargoItems,
    legs: Array.isArray(mission.legs) ? mission.legs : [],
    departureCity,
    departureState,
    arrivalCity,
    arrivalState,
    departure: formatMissionEndpoint(
      mission.departure,
      departureCity,
      departureState,
    ),
    arrival: formatMissionEndpoint(mission.arrival, arrivalCity, arrivalState),
    descriptionPlain,
    description: descriptionPlain,
    type,
    missionCategory,
    postedBy: mission.postedBy || mission.owner?.name || "",
  };
}
